/** 列優先（WebGPU / WGSL の mat4x4f と同じ並び）の 4x4 行列 */
export type Mat4 = Float32Array;

export function mat4(): Mat4 {
  const m = new Float32Array(16);
  m[0] = m[5] = m[10] = m[15] = 1;
  return m;
}

/** 右手系の透視投影。深度は WebGPU の [0, 1] */
export function perspective(out: Mat4, fovY: number, aspect: number, near: number, far: number) {
  const f = 1 / Math.tan(fovY / 2);
  out.fill(0);
  out[0] = f / aspect;
  out[5] = f;
  out[10] = far / (near - far);
  out[11] = -1;
  out[14] = (far * near) / (near - far);
  return out;
}

export function lookAt(
  out: Mat4,
  eye: readonly number[],
  target: readonly number[],
  up: readonly number[],
) {
  let zx = eye[0]! - target[0]!,
    zy = eye[1]! - target[1]!,
    zz = eye[2]! - target[2]!;
  let len = Math.hypot(zx, zy, zz) || 1;
  zx /= len;
  zy /= len;
  zz /= len;
  let xx = up[1]! * zz - up[2]! * zy,
    xy = up[2]! * zx - up[0]! * zz,
    xz = up[0]! * zy - up[1]! * zx;
  len = Math.hypot(xx, xy, xz) || 1;
  xx /= len;
  xy /= len;
  xz /= len;
  const yx = zy * xz - zz * xy,
    yy = zz * xx - zx * xz,
    yz = zx * xy - zy * xx;
  out[0] = xx;
  out[1] = yx;
  out[2] = zx;
  out[3] = 0;
  out[4] = xy;
  out[5] = yy;
  out[6] = zy;
  out[7] = 0;
  out[8] = xz;
  out[9] = yz;
  out[10] = zz;
  out[11] = 0;
  out[12] = -(xx * eye[0]! + xy * eye[1]! + xz * eye[2]!);
  out[13] = -(yx * eye[0]! + yy * eye[1]! + yz * eye[2]!);
  out[14] = -(zx * eye[0]! + zy * eye[1]! + zz * eye[2]!);
  out[15] = 1;
  return out;
}

export function multiply(out: Mat4, a: Mat4, b: Mat4) {
  for (let c = 0; c < 4; c++) {
    for (let r = 0; r < 4; r++) {
      let s = 0;
      for (let k = 0; k < 4; k++) s += a[k * 4 + r]! * b[c * 4 + k]!;
      out[c * 4 + r] = s;
    }
  }
  return out;
}

/** 点 (x, y, z, 1) を変換し、同次座標 [x, y, z, w] を返す */
export function transformPoint(
  m: Mat4,
  x: number,
  y: number,
  z: number,
): [number, number, number, number] {
  return [
    m[0]! * x + m[4]! * y + m[8]! * z + m[12]!,
    m[1]! * x + m[5]! * y + m[9]! * z + m[13]!,
    m[2]! * x + m[6]! * y + m[10]! * z + m[14]!,
    m[3]! * x + m[7]! * y + m[11]! * z + m[15]!,
  ];
}
